import { ReactElement } from 'react';
import {
  ProductFamily,
  Product,
  ProductFamilyName,
  ProductName,
  ProductPlan,
  UnitName
} from './models';
import { Logo } from '@atlassiansox/wpl-utils';

export const UNLIMITED_USERS = 500000; //Arbitrarily large number for "Unlimited Users" - set to 500K
export const DEFAULT_MAX_UNIT_LIMIT = 35000;
const jiraProductsFamilyLogo = Logo({
  brand: 'jira',
  type: 'mark',
  appearance: 'inverse'
});
const jiraSoftwareLogo = Logo({
  brand: 'jirasoftware',
  type: 'mark',
  appearance: 'brand'
});
const jiraServiceManagementLogo = Logo({
  brand: 'jiraproductdiscovery',
  type: 'mark',
  appearance: 'brand'
});
const jiraProductDiscoveryLogo = Logo({
  brand: 'jiraservicemanagement',
  type: 'mark',
  appearance: 'brand'
});

const confluenceProductFamilyLogo = Logo({
  brand: 'confluence',
  type: 'mark',
  appearance: 'inverse'
});
const confluenceLogo = Logo({
  brand: 'jira',
  type: 'mark',
  appearance: 'brand'
});

const bitbucketProductFamilyLogo = Logo({
  brand: 'bitbucket',
  type: 'mark',
  appearance: 'inverse'
});
const bitbucketLogo = Logo({
  brand: 'bitbucket',
  type: 'mark',
  appearance: 'brand'
});

const compassProductFamilyLogo = Logo({
  brand: 'compass',
  type: 'mark',
  appearance: 'inverse'
});
const compassLogo = Logo({
  brand: 'compass',
  type: 'mark',
  appearance: 'brand'
});

const atlassianGuardFamilyLogo = Logo({
  brand: 'atlassianaccess',
  type: 'mark',
  appearance: 'inverse'
});
const atlassianGuardLogo = Logo({
  brand: 'atlassianaccess',
  type: 'mark',
  appearance: 'brand'
});

export const atlassianLogo = Logo({
  brand: 'atlassian',
  type: 'logo',
  appearance: 'brand'
});

export const PRODUCT_FAMILY_NAME_BY_PRODUCT_NAME: Record<
  ProductName,
  ProductFamilyName
> = {
  ['Jira']: 'Jira products',
  ['Jira Service Management']: 'Jira products',
  ['Jira Product Discovery']: 'Jira products',
  ['Confluence']: 'Confluence',
  ['Bitbucket']: 'Bitbucket',
  ['Compass']: 'Compass',
  ['Atlassian Guard']: 'Atlassian Guard'
};

export const PRODUCT_NAMES_BY_PRODUCT_FAMILY_NAME: Record<
  ProductFamilyName,
  ProductName[]
> = {
  'Jira products': [
    'Jira',
    'Jira Service Management',
    'Jira Product Discovery'
  ],
  'Confluence': ['Confluence'],
  'Bitbucket': ['Bitbucket'],
  'Compass': ['Compass'],
  'Atlassian Guard': ['Atlassian Guard']
};

export const PRODUCT_FAMILIES_BY_NAME: Record<
  ProductFamilyName,
  ProductFamily
> = {
  'Jira products': {
    name: 'Jira products',
    icon: jiraProductsFamilyLogo,
    hasApps: true,
    appsLimit: 50000
  },
  'Confluence': {
    name: 'Confluence',
    icon: confluenceLogo,
    hasApps: true,
    appsLimit: 150000
  },
  'Bitbucket': {
    name: 'Bitbucket',
    icon: bitbucketLogo,
    hasApps: true
  },
  'Compass': {
    name: 'Compass',
    icon: compassLogo
  },
  'Atlassian Guard': {
    name: 'Atlassian Guard',
    icon: atlassianGuardLogo,
    hasApps: false
  }
};

export const PRODUCTS_BY_FAMILY_NAME: Record<ProductFamilyName, Product[]> = {
  'Jira products': [
    {
      name: 'Jira',
      icon: jiraSoftwareLogo,
      marketplaceAppKey: 'jira-software.ondemand',
      unitName: 'USER',
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 10],
          ccpOfferingSKU: 'jira-software-free',
          hamsProductKey: 'jira-software.ondemand.free'
        },
        {
          name: 'Standard',
          unitLimits: [1, 50000],
          ccpOfferingSKU: 'jira-software-standard',
          hamsProductKey: 'jira-software.ondemand'
        },
        {
          name: 'Premium',
          unitLimits: [1, 50000],
          ccpOfferingSKU: 'jira-software-premium',
          hamsProductKey: 'jira-software.ondemand.premium'
        },
        {
          name: 'Enterprise',
          unitLimits: [801, 50000],
          ccpOfferingSKU: 'jira-software-enterprise-sku',
          hamsProductKey: 'jira-software.ondemand.enterprise'
        }
      ]
    },
    {
      name: 'Jira Service Management',
      icon: jiraServiceManagementLogo,
      unitName: 'AGENT',
      marketplaceAppKey: 'jira-servicedesk.ondemand',
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 3],
          ccpOfferingSKU: 'jira-servicedesk-free',
          hamsProductKey: 'jira-servicedesk.ondemand.free'
        },
        {
          name: 'Standard',
          unitLimits: [1, 20000],
          ccpOfferingSKU: 'jira-servicedesk-standard-repack-sku',
          hamsProductKey: 'jira-servicedesk.ondemand'
        },
        {
          name: 'Premium',
          unitLimits: [1, 20000],
          ccpOfferingSKU: 'jira-servicedesk-premium',
          hamsProductKey: 'jira-servicedesk.ondemand.premium'
        },
        {
          name: 'Enterprise',
          unitLimits: [201, 20000],
          ccpOfferingSKU: 'jira-servicedesk-enterprise-sku',
          hamsProductKey: 'jira-servicedesk.ondemand.enterprise'
        }
      ]
    },
    {
      name: 'Jira Product Discovery',
      icon: jiraProductDiscoveryLogo,
      unitName: 'CREATOR',
      // Using JSW key as it should be the same key for all familyProducts of JIRA and there is no hamsProductKey for marketplace apps
      marketplaceAppKey: 'jira-software.ondemand',
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 3],
          ccpOfferingSKU: 'jira-product-discovery-free',
          hamsProductKey: ''
        },
        {
          name: 'Standard',
          unitLimits: [3, 5000],
          ccpOfferingSKU: 'jira-product-discovery-standard',
          hamsProductKey: ''
        },
        {
          name: 'Premium',
          unitLimits: [3, 5000],
          ccpOfferingSKU: 'jira-product-discovery-premium-sku',
          hamsProductKey: ''
        }
      ]
    }
  ],
  'Confluence': [
    {
      name: 'Confluence',
      icon: confluenceLogo,
      unitName: 'USER',
      marketplaceAppKey: 'pricingplan.confluence.ondemand',
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 10],
          ccpOfferingSKU: 'confluence-free',
          hamsProductKey: 'pricingplan.confluence.ondemand.free'
        },
        {
          name: 'Standard',
          unitLimits: [1, 150000],
          ccpOfferingSKU: 'confluence-standard',
          hamsProductKey: 'pricingplan.confluence.ondemand'
        },
        {
          name: 'Premium',
          unitLimits: [1, 150000],
          ccpOfferingSKU: 'confluence-premium',
          hamsProductKey: 'pricingplan.confluence.ondemand.premium'
        },
        {
          name: 'Enterprise',
          unitLimits: [801, 150000],
          ccpOfferingSKU: 'confluence-enterprise-sku',
          hamsProductKey: 'pricingplan.confluence.ondemand.enterprise'
        }
      ]
    }
  ],
  'Bitbucket': [
    {
      name: 'Bitbucket',
      icon: bitbucketLogo,
      unitName: 'USER',
      marketplaceAppKey: 'com.atlassian.bitbucket.manual',
      noAnnualBilling: true,
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 5],

          ccpOfferingSKU: '',
          hamsProductKey: 'com.atlassian.bitbucket.manual.free'
        },
        {
          name: 'Standard',
          unitLimits: [1, UNLIMITED_USERS],
          ccpOfferingSKU: '',
          hamsProductKey: 'com.atlassian.bitbucket.manual'
        },
        {
          name: 'Premium',
          unitLimits: [1, UNLIMITED_USERS],
          ccpOfferingSKU: '',
          hamsProductKey: 'com.atlassian.bitbucket.manual.premium'
        }
      ]
    }
  ],
  'Compass': [
    {
      name: 'Compass',
      icon: compassLogo,
      unitName: 'USER',
      plans: [
        {
          name: 'Free',
          unitLimits: [1, 3],

          ccpOfferingSKU: 'compass-free',
          hamsProductKey: ''
        },
        {
          name: 'Standard',
          unitLimits: [1, DEFAULT_MAX_UNIT_LIMIT],
          ccpOfferingSKU: 'compass-standard',
          hamsProductKey: ''
        }
      ]
    }
  ],
  'Atlassian Guard': [
    {
      name: 'Atlassian Guard',
      icon: atlassianGuardLogo,
      unitName: 'USER',
      plans: [
        {
          name: 'Standard',
          unitLimits: [1, 40000],
          ccpOfferingSKU: 'atlassian-access-standard-sku',
          hamsProductKey: ''
        },
        {
          name: 'Premium',
          unitLimits: [1, 40000],
          ccpOfferingSKU: 'atlassian-guard-premium-addon-sku',
          hamsProductKey: ''
        }
      ]
    }
  ]
};

export const PRODUCT_FAMILY_ICONS_BY_NAME: Record<
  ProductFamilyName,
  ReactElement
> = {
  'Jira products': jiraProductsFamilyLogo,
  'Confluence': confluenceProductFamilyLogo,
  'Bitbucket': bitbucketProductFamilyLogo,
  'Compass': compassProductFamilyLogo,
  'Atlassian Guard': atlassianGuardFamilyLogo
};

export const PRODUCTS = Object.values(PRODUCTS_BY_FAMILY_NAME).flat();

export const PRODUCT_FAMILY_NAMES = Object.keys(PRODUCTS_BY_FAMILY_NAME);
export const PRODUCT_NAMES = PRODUCTS.map((product) => product.name);

export const MAPPED_PRODUCT_FAMILIES_BY_NAME = Object.values(
  PRODUCT_FAMILIES_BY_NAME
).flat();

export const PLANS_BY_PRODUCT_NAME: Partial<
  Record<ProductName, ProductPlan[]>
> = PRODUCTS.reduce(
  (plansByProductName, { name, plans }) => ({
    ...plansByProductName,
    [name]: plans
  }),
  {}
);

export const UNIT_NAME_BY_PRODUCT_NAME: Partial<Record<ProductName, UnitName>> =
  PRODUCTS.reduce(
    (plansByProductName, { name, unitName }) => ({
      ...plansByProductName,
      [name]: unitName
    }),
    {}
  );

export const ICONS_BY_PRODUCT_NAME: Partial<Record<ProductName, ReactElement>> =
  PRODUCTS.reduce(
    (iconsByProductName, { name, icon }) => ({
      ...iconsByProductName,
      [name]: icon
    }),
    {}
  );

export const APPS_BY_PRODUCT_NAME: Partial<Record<ProductName, string>> =
  PRODUCTS.reduce(
    (appsByProductName, { name, marketplaceAppKey }) => ({
      ...appsByProductName,
      [name]: marketplaceAppKey
    }),
    {}
  );

export const HAS_NO_ANNUAL_BY_PRODUCT: Partial<Record<ProductName, boolean>> =
  PRODUCTS.reduce(
    (appsByProductName, { name, noAnnualBilling }) => ({
      ...appsByProductName,
      [name]: noAnnualBilling
    }),
    {}
  );

export const HAS_APPS_BY_PRODUCT_FAMILY: Partial<
  Record<ProductFamilyName, string>
> = MAPPED_PRODUCT_FAMILIES_BY_NAME.reduce(
  (appsByProductName, { name, hasApps }) => ({
    ...appsByProductName,
    [name]: hasApps
  }),
  {}
);

export const APPS_LIMIT_BY_PRODUCT_FAMILY: Partial<
  Record<ProductFamilyName, number>
> = MAPPED_PRODUCT_FAMILIES_BY_NAME.reduce(
  (appsByProductName, { name, appsLimit }) => ({
    ...appsByProductName,
    [name]: appsLimit
  }),
  {}
);

export const ANALYTICS_EVENTS = {
  updateProductList: 'update_product_list',
  changeProductEdition: 'change_product_edition',
  userCount: 'update_user_count_field',
  openProductSelection: 'open_product_selector',
  selectProduct: 'select_product',
  openAppSelection: 'open_marketplace_app_selector',
  selectApp: 'select_marketplace_app',
  removeApp: 'remove_2p_app',
  removeProduct: 'remove_1p_product',
  billingCycle: 'change_billing_cycle',
  contactSales: 'contact_sales',
  downloadPdf: 'download_pdf',
  restartFlow: 'restart_flow',
  appErrored: 'marketplace_app_errored'
};
